import React from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LogoMark from '../components/LogoMark';
import {
  LayoutDashboard,
  Settings,
  LogOut,
  Menu,
  X,
  ShieldCheck,
  Users
} from 'lucide-react';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Overview' },
    { path: '/dashboard/governance-review', icon: ShieldCheck, label: 'Governance Review' },
    { path: '/dashboard/leads', icon: Users, label: 'Leads' },
  ];

  const navLinkClass = (isActive, isDisabled) =>
    `flex items-center justify-between gap-3 px-4 py-3 font-bold transition-all border-2 ${
      isActive
        ? 'bg-lime border-ink/10 shadow-saas-sm'
        : isDisabled
        ? 'border-transparent text-ink/40 cursor-not-allowed'
        : 'border-transparent hover:border-ink/10 hover:bg-white'
    }`;

  return (
    <div className="brand-theme min-h-screen flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex md:flex-col md:w-64 bg-white border-r border-ink/10">
        <div className="p-6 border-b border-ink/10">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <LogoMark />
            <span className="font-bold text-lg tracking-tight text-ink">GovernWell</span>
          </Link>
          <p className="text-sm font-bold uppercase tracking-wide text-ink/60 mt-2 truncate">{user?.orgName || 'Organization'}</p>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const isDisabled = item.locked;

            return (
              <Link
                key={item.path}
                to={isDisabled ? '#' : item.path}
                className={navLinkClass(isActive, isDisabled)}
                onClick={(e) => isDisabled && e.preventDefault()}
              >
                <div className="flex items-center gap-3">
                  <Icon size={20} />
                  <span className="text-sm">{item.label}</span>
                </div>
                {isDisabled && (
                  <span className="text-xs font-bold uppercase px-2 py-0.5 bg-ink text-white">
                    Locked
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-ink/10 space-y-2">
          <Link
            to="/dashboard/settings"
            className={navLinkClass(location.pathname === '/dashboard/settings')}
          >
            <div className="flex items-center gap-3">
              <Settings size={20} />
              <span className="text-sm">Settings</span>
            </div>
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 font-bold border-2 border-transparent hover:border-ink/10 hover:bg-red-500 hover:text-white transition-all"
          >
            <LogOut size={20} />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-cream border-b border-ink/10 p-4">
        <div className="flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <LogoMark />
            <span className="font-bold text-lg tracking-tight text-ink">GovernWell</span>
          </Link>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <nav className="mt-4 space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const isDisabled = item.locked;

              return (
                <Link
                  key={item.path}
                  to={isDisabled ? '#' : item.path}
                  className={navLinkClass(isActive, isDisabled)}
                  onClick={(e) => {
                    if (isDisabled) e.preventDefault();
                    setIsMobileMenuOpen(false);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <Icon size={20} />
                    <span className="text-sm">{item.label}</span>
                  </div>
                  {isDisabled && (
                    <span className="text-xs font-bold uppercase px-2 py-0.5 bg-ink text-white">
                      Locked
                    </span>
                  )}
                </Link>
              );
            })}
            <Link
              to="/dashboard/settings"
              className={navLinkClass(location.pathname === '/dashboard/settings')}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="flex items-center gap-3">
                <Settings size={20} />
                <span className="text-sm">Settings</span>
              </div>
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 font-bold border-2 border-ink/10 bg-red-500 text-white transition-all"
            >
              <LogOut size={20} />
              <span className="text-sm">Logout</span>
            </button>
          </nav>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 md:ml-0 mt-16 md:mt-0 bg-cream">
        <Outlet />
      </main>
    </div>
  );
};

export default DashboardLayout;
